<template>
  <v-text-field
    v-model="modelValue"
    outlined
    dense
    hide-details="auto"
    :disabled="disabled"
    :label="label"
    :placeholder="placeholder"
    :persistent-placeholder="!!placeholder"
    :rules="rules"
    :append-icon="appendIcon"
    :type="computedType"
    :hint="hint"
    :persistent-hint="!!hint"
    :suffix="suffix"
    :min="min"
    :max="max"
    @keydown="handleInput($event)"
  />
</template>

<script>
import { PropOptions, ComputedOptions } from 'vue'
import { Computed } from '@/_typedefs'
import { maska } from 'maska'

export default {
  name: 'MaskedTextInputComponent',
  props: {
    value: {
      type: [String, Number],
      validator: (v) => ['string', 'number'].includes(typeof v) || v === null,
    },
    label: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    appendIcon: {
      type: String,
      default: null,
    },
    /** @type {PropOptions<'text' | 'number' | 'tel'>} */
    type: {
      type: String,
      default: 'text',
      validator: (v) => ['text', 'number', 'tel'].includes(v),
    },
    /** @type {PropOptions<Array<(v: any) => boolean | string>>} */
    rules: {
      type: Array,
      default: () => [],
    },
    /** @type {PropOptions<string | string[]>} */
    mask: {
      type: [Array, String],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hint: {
      type: String,
      default: null,
    },
    suffix: {
      type: String,
      default: null,
    },
    max: {
      type: [Number, String],
      default: null,
    },
    min: {
      type: [Number, String],
      default: null,
    },
  },
  directives: { maska },
  computed: {
    /** @type {ComputedOptions<string | number>} */
    modelValue: {
      get() {
        return this.value
      },
      set(v) {
        this.$emit('input', v)
      },
    },
    /** @type {Computed<'text' | 'number' | 'tel'>} */
    computedType() {
      if (this.type === 'number' && !!this.mask) {
        return 'tel'
      }
      return this.type
    },
  },
  methods: {
    handleInput(e) {
      let newValue = e.target.value
      const key = e.key

      if (key == 'Backspace') {
        e.target.value = newValue.slice(0, newValue.length)
        return
      }

      if (key == 'ArrowLeft' && e.target.selectionStart > 0) {
        e.target.selectionStart--
        return
      }

      if (key == 'ArrowRight' && e.target.selectionStart < e.target.value + 1) {
        e.target.selectionStart++
        return
      }

      const specialSymbols = ['!', '@', '#', '$', '%', '&', '*', '+', '=', '~', '^', '-', '_', ',', '.']
      const specialRegex = new RegExp(`[${specialSymbols.join()}]`)
      const numberRegex = /[0-9]/
      const letterRegex = /[A-Za-z]/
      let numberCount = 0,
        specialCount = 0

      if (letterRegex.test(key)) {
        e.preventDefault()
        e.target.value = newValue.slice(0, newValue.length)
        return
      }

      if (newValue.length > 6) e.preventDefault()

      for (let i = 0; i < newValue.length; i++) {
        if (numberRegex.test(newValue[i])) numberCount++
        if (specialRegex.test(newValue[i])) specialCount++

        if (numberCount > 3 && numberRegex.test(key)) {
          e.preventDefault()
          //newValue = newValue.slice(0, i - 1)
          numberCount--
          return
        }

        if (specialCount >= 2 && specialRegex.test(key)) {
          e.preventDefault()
          //newValue = newValue.slice(0, i - 1)
          specialCount--
          return
        }
      }
    },
  },
}
</script>

<style scoped>
:deep(.v-input__slot) {
  min-height: 48px !important;
  font-size: 14px;
}

:deep(.v-text-field__details) {
  margin: 0 !important;
}

/* Chrome, Safari, Edge, Opera */
:deep(input::-webkit-outer-spin-button),
:deep(input::-webkit-inner-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
:deep(input[type='number']) {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
